import { UseQueryResult, useQuery } from "@tanstack/react-query";
import {
  TixologiTokenExchange,
  TixologiTokenScope,
} from "../../models/tixologi-token";

import { CLIENT_exchangeTixologiToken } from "../../client/user/tixologi-token";

export const tixologiTokenExchangeQueryKey = (
  scope: TixologiTokenScope,
  id: string | undefined
) => ["tixologiTokenExchange", scope, id] as const;

type UseTixologiTokenExchangeProps = {
  scope: TixologiTokenScope;
  /**
   * The comedian id (ComedianManager) or venue_page id (VenueManager) the
   * token should be scoped to. Required for the manager scopes — the query
   * stays disabled without it. Ignored for the User scope.
   */
  id?: string;
};

/**
 * Exchanges the logged-in Punchup session for a Tixologi token, scoped by
 * `TixologiTokenScope`. Successor of the legacy `useTixologi({ id,
 * isComedian })` — the boolean is replaced by the explicit scope enum, which
 * also expresses the email-only (no partner) exchange the boolean couldn't.
 *
 * Returns a standard React Query result: `data` is `{ token, partnerId?,
 * greenZone }`, failures land in `error`, and `refetch` forces a fresh
 * exchange (e.g. after a 401 on a Tixologi call).
 */
export function useTixologiTokenExchange({
  scope,
  id,
}: UseTixologiTokenExchangeProps): UseQueryResult<
  TixologiTokenExchange,
  Error
> {
  return useQuery({
    queryKey: tixologiTokenExchangeQueryKey(scope, id),
    queryFn: () => CLIENT_exchangeTixologiToken(scope, id),
    // Tixologi tokens expire after 1 hour; re-exchange just before that.
    staleTime: 1000 * 60 * 50,
    enabled: scope === TixologiTokenScope.User || !!id,
  });
}
